import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhTW from 'antd/locale/zh_TW';
import { darkThemeConfig } from './theme/darkTheme';
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './components/Common/ProtectedRoute';
import ErrorBoundary from './components/Common/ErrorBoundary';
import useIdleTimer from './hooks/useIdleTimer';
import useTokenRefresh from './hooks/useTokenRefresh';
import useSystemStore from './stores/systemStore';
import useDeviceCatalogStore from './stores/deviceCatalogStore';
import useCalibrationStore from './stores/calibrationStore';
import useAuthStore from './stores/authStore';
import UpdateNotification from './components/Common/UpdateNotification';

// chunk 載入失敗時自動重載頁面（PWA 部署後舊 chunk hash 失效）
function lazyWithRetry(importFn: () => Promise<any>) {
  return React.lazy(() =>
    importFn().catch((error) => {
      const retried = sessionStorage.getItem('chunk_retry');
      if (!retried) {
        sessionStorage.setItem('chunk_retry', '1');
        window.location.reload();
        return new Promise(() => {}); // 頁面即將重載，不 resolve
      }
      sessionStorage.removeItem('chunk_retry');
      throw error; // 重試後仍失敗，交給 ErrorBoundary
    })
  );
}

const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const InfoPage = lazyWithRetry(() => import('./pages/InfoPage'));
const MonitoringPage = lazyWithRetry(() => import('./pages/MonitoringPage'));
// AI 振動量測：直讀 prognosis DB 的 EHI 健康卡片（取代美濃 VibrationPage）
const VibrationPage = lazyWithRetry(() => import('./pages/BrineVibrationPage'));
const EnergyPage = lazyWithRetry(() => import('./pages/EnergyPage'));
const AlarmPage = lazyWithRetry(() => import('./pages/AlarmPage'));
const HistoryPage = lazyWithRetry(() => import('./pages/HistoryPage'));
const SystemPage = lazyWithRetry(() => import('./pages/SystemPage'));
const ReportPage = lazyWithRetry(() => import('./pages/ReportPage'));
const FloorPlanPage = lazyWithRetry(() => import('./pages/FloorPlanPage'));
const AuditPage = lazyWithRetry(() => import('./pages/AuditPage'));
const SchedulePage = lazyWithRetry(() => import('./pages/SchedulePage'));
const StandaloneTrendPage = lazyWithRetry(() => import('./pages/StandaloneTrendPage'));

const PageLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 300, background: 'var(--sw-bg)' }}>
    <Spin size="large" />
  </div>
);

export default function App() {
  useIdleTimer();
  useTokenRefresh();
  const fetchBranding = useSystemStore((s) => s.fetchBranding);
  const fetchCatalog = useDeviceCatalogStore((s) => s.fetchCatalog);
  const fetchCalibration = useCalibrationStore((s) => s.fetchConfig);
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    // 成功載入後清除 chunk 重試旗標（避免殘留影響下次載入）
    sessionStorage.removeItem('chunk_retry');
    // branding 為公開端點（登入頁 logo 需用），無條件載入
    fetchBranding();
  }, [fetchBranding]);

  useEffect(() => {
    // catalog / calibration 為受保護端點：未登入時不呼叫，避免登入頁噴 401 紅字。
    // 登入後 token 由 null 變哨兵 → 本 effect 重跑並補載。
    if (!token) return;
    fetchCatalog();
    fetchCalibration();
  }, [token, fetchCatalog, fetchCalibration]);

  return (
    <ConfigProvider locale={zhTW} theme={darkThemeConfig}>
      <ErrorBoundary>
        <UpdateNotification />
        <BrowserRouter>
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* 趨勢獨立看板：需登入但無側邊欄／頁首（由「另開視窗」開啟） */}
              <Route
                path="/history/standalone"
                element={
                  <ProtectedRoute>
                    <StandaloneTrendPage />
                  </ProtectedRoute>
                }
              />
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                {/* 首頁導向「冷藏系統」(/monitoring)，系統圖為其第一個分頁，避免重複 */}
                <Route path="/" element={<Navigate to="/monitoring" replace />} />
                <Route path="/monitoring" element={<MonitoringPage />} />
                <Route path="/schedule" element={<SchedulePage />} />
                <Route path="/vibration" element={<VibrationPage />} />
                <Route path="/energy" element={<EnergyPage />} />
                <Route path="/trend" element={<Navigate to="/history" replace />} />
                <Route path="/alarm" element={<AlarmPage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/report" element={<ReportPage />} />
                <Route path="/floor-plan" element={<FloorPlanPage />} />
                <Route path="/audit" element={<AuditPage />} />
                <Route path="/system" element={<SystemPage />} />
                <Route path="/info" element={<InfoPage />} />
                {/* 舊書籤導向新位置（2026-10-17 關於／下載 搬到 /info）。
                    /system?tab=about|download 不需在此處理：tabRegistry 已移除這兩個
                    分頁鍵，SystemPage 會 fallback 到通訊狀態，不會出現空白頁。 */}
                <Route path="/manual" element={<Navigate to="/info?tab=manual" replace />} />
                <Route path="/app-download" element={<Navigate to="/info?tab=download" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </ConfigProvider>
  );
}
